import { useCallback, useEffect, useState } from 'react'
import Loading from 'atoms/Loading'
import { toast } from 'atoms/Toast'
import LSDate, { DATE_FORMAT_MMDDYYYYHHMMSS_AMPM } from 'common/Date.helpers'
import { MessageRead, MessageLogRead } from 'common/types/kraken-core/Message'
import { readMessageLogs } from 'services/kraken-core/messages/messages.service'
import useCancelToken from 'hooks/useCancelToken'
import { Layout, Table, Text, Tooltip } from '@loadsmart/loadsmart-ui'
import Icon from 'atoms/Icon'
import { Themes } from '@loadsmart/loadsmart-ui/dist/theming'

export interface MessageLogsProps {
  message: MessageRead
}

const MessageLogsView = ({ message }: MessageLogsProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [logs, setLogs] = useState<MessageLogRead[]>([])

  const { getSource, clearSource, cancelPending, isCancel } = useCancelToken()

  const logLevelStyle = useCallback((level: string) => {
    const mapping: any = {
      ERROR: {
        style: {
          backgroundColor: Themes.Loadsmart['color-danger-light'],
        },
        symbol: (
          <Tooltip message="Error">
            <Icon name="close" />
          </Tooltip>
        ),
      },
      WARNING: {
        style: {
          backgroundColor: Themes.Loadsmart['color-warning-light'],
        },
        symbol: (
          <Tooltip message="Warning">
            <Icon name="warning" />
          </Tooltip>
        ),
      },
    }
    return mapping[level] || ''
  }, [])

  useEffect(() => {
    const source = getSource()

    ;(async () => {
      const [error, response] = await readMessageLogs(message.id, {
        cancelToken: source.token,
      })

      clearSource()
      setIsLoading(false)

      if (error && !isCancel) return toast.error(`Something went wrong: ${error}`)

      return setLogs(response?.data || [])
    })()

    return () => cancelPending()
  }, [])

  return (
    <Layout.Box background="neutral-white">
      {isLoading && <Loading className="justify-center mt-8" />}
      {!isLoading && (
        <Table scale="default" data-testid="message-logs-list">
          <Table.Head data-testid="message-logs-list-head">
            <Table.Cell>Created At</Table.Cell>
            <Table.Cell>Log</Table.Cell>
          </Table.Head>
          <Table.Body data-testid="message-logs-list-body" className="break-all">
            {logs?.map(log => {
              const levelStyle: any = logLevelStyle(log.log_level)
              const createdAt = LSDate(log.created_at)?.format(DATE_FORMAT_MMDDYYYYHHMMSS_AMPM)
              return (
                <Table.Row key={`${log.created_at}-${log.log}`} style={levelStyle.style}>
                  <Table.Cell>
                    <Text>{createdAt}</Text>
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'break-spaces' }}>
                    <Layout.Group>
                      {levelStyle.symbol}
                      {log.log}
                    </Layout.Group>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      )}
    </Layout.Box>
  )
}

export default MessageLogsView
